import { FEATURE_FLAGS } from "./featureFlag.js";

export const SETUP_STATE = {
  DONE: "done",
  TODO: "todo",
  BLOCKED: "blocked",
  SKIPPED: "skipped",
};

export const SETUP_CHECK = { FIELD: "field", COUNT: "count" };

export const DEFAULT_CATEGORY_OF_USER_TAG_TITLES = [
  "Speaker",
  "Sponsor",
  "Organizer",
  "Attendee",
];

export const SETUP_STEPS = [
  {
    key: "essentials",
    label: "Essentials",
    hint: "Event name and main language",
    featureFlag: null,
    dependsOn: [],
    tasks: [
      {
        key: "title",
        label: "Event title",
        required: true,
        check: { type: SETUP_CHECK.FIELD, path: "event.name" },
      },
      {
        key: "language",
        label: "Main language",
        required: true,
        check: { type: SETUP_CHECK.FIELD, path: "event.language" },
      },
    ],
  },
  {
    key: "details",
    label: "Event details & dates",
    hint: "Location, contact email and the days your event runs",
    featureFlag: null,
    dependsOn: [],
    tasks: [
      {
        key: "dates",
        label: "Event dates",
        required: true,
        hint: "Sessions can't be scheduled until the event days exist.",
        check: { type: SETUP_CHECK.COUNT, resource: "eventDates", min: 1 },
      },
      {
        key: "location",
        label: "Main location",
        required: true,
        check: { type: SETUP_CHECK.FIELD, path: "event.mainLocation" },
      },
      {
        key: "contact",
        label: "Contact email",
        check: { type: SETUP_CHECK.FIELD, path: "event.contactEmail" },
      },
    ],
  },
  {
    key: "brand",
    label: "Make it yours",
    hint: "Accent color, logo and header image",
    featureFlag: null,
    dependsOn: [],
    tasks: [
      {
        key: "accentColor",
        label: "Accent color",
        check: { type: SETUP_CHECK.FIELD, path: "event.config.accentColor" },
      },
      {
        key: "logo",
        label: "Logo image",
        check: { type: SETUP_CHECK.FIELD, path: "event.config.logoImage" },
      },
      {
        key: "header",
        label: "Header image",
        check: { type: SETUP_CHECK.FIELD, path: "event.config.headerImage" },
      },
    ],
  },
  {
    key: "access",
    label: "Give people access",
    hint: "Import your attendee list, or open registration",
    featureFlag: null,
    dependsOn: [],
    tasks: [
      {
        key: "holders",
        label: "Ticket holders",
        check: { type: SETUP_CHECK.COUNT, resource: "tickets", min: 1 },
      },
      {
        key: "ticketTypes",
        label: "Registration ticket types",
        featureFlag: FEATURE_FLAGS.REGISTRATIONS,
        check: {
          type: SETUP_CHECK.COUNT,
          resource: "registrationTicketTypes",
          min: 1,
        },
      },
      {
        key: "registrationOpen",
        label: "Registration open date",
        featureFlag: FEATURE_FLAGS.REGISTRATIONS,
        dependsOn: ["ticketTypes"],
        check: {
          type: SETUP_CHECK.FIELD,
          path: "registrationsSettings.registrationsOpenDate",
        },
      },
    ],
  },
  {
    key: "agenda",
    label: "Fill the agenda",
    hint: "Locations, speakers, sponsors and sessions",
    featureFlag: null,
    dependsOn: ["details"],
    tasks: [
      {
        key: "locations",
        label: "Locations",
        check: { type: SETUP_CHECK.COUNT, resource: "locations", min: 1 },
      },
      {
        key: "speakers",
        label: "Speakers",
        check: { type: SETUP_CHECK.COUNT, resource: "speakers", min: 1 },
      },
      {
        key: "sponsorLevels",
        label: "Sponsor levels",
        check: { type: SETUP_CHECK.COUNT, resource: "sponsorLevels", min: 1 },
      },
      {
        key: "sponsors",
        label: "Sponsors",
        dependsOn: ["sponsorLevels"],
        check: { type: SETUP_CHECK.COUNT, resource: "sponsors", min: 1 },
      },
      {
        key: "sessions",
        label: "Sessions",
        dependsOn: ["locations"],
        check: { type: SETUP_CHECK.COUNT, resource: "sessions", min: 1 },
      },
    ],
  },
  {
    key: "engage",
    label: "Go further",
    hint: "Optional",
    optional: true,
    featureFlag: null,
    dependsOn: [],
    tasks: [
      {
        key: "tags",
        label: "Tags",
        check: { type: SETUP_CHECK.COUNT, resource: "tags", min: 1 },
      },
      {
        key: "pages",
        label: "Pages",
        check: { type: SETUP_CHECK.COUNT, resource: "pages", min: 1 },
      },
      {
        key: "announcements",
        label: "Announcements",
        check: { type: SETUP_CHECK.COUNT, resource: "announcements", min: 1 },
      },
      {
        key: "polls",
        label: "Polls",
        featureFlag: FEATURE_FLAGS.POLLS,
        check: { type: SETUP_CHECK.COUNT, resource: "polls", min: 1 },
      },
      {
        key: "surveys",
        label: "Surveys",
        featureFlag: FEATURE_FLAGS.SURVEYS,
        check: { type: SETUP_CHECK.COUNT, resource: "surveys", min: 1 },
      },
    ],
  },
];

export const getEventSetupProgressSchema = {
  params: {
    type: "object",
    properties: { id: { type: "number" } },
    required: ["id"],
  },
};

const isFlagEnabled = (featureFlag, flags) =>
  featureFlag == null || flags.has(featureFlag);

const readPath = (context, path) =>
  path.split(".").reduce((value, key) => value?.[key], context);

const isCheckPassed = (check, context) => {
  if (check.type === SETUP_CHECK.COUNT) {
    return (Number(context.counts?.[check.resource]) || 0) >= check.min;
  }
  return Boolean(readPath(context, check.path));
};

const isResolved = (state) =>
  state === SETUP_STATE.DONE || state === SETUP_STATE.SKIPPED;

const summarize = (items) => ({
  done: items.filter((item) => isResolved(item.state)).length,
  total: items.length,
  requiredTotal: items.filter((item) => item.required).length,
  requiredRemaining: items.filter(
    (item) => item.required && item.state !== SETUP_STATE.DONE
  ).length,
});

const findBlockers = (dependsOn = [], states) =>
  dependsOn.filter(
    (dep) => states.has(dep) && states.get(dep) !== SETUP_STATE.DONE
  );

const evaluateTask = (step, task, context, skipped, taskStates) => {
  const key = `${step.key}.${task.key}`;
  const required = Boolean(task.required);
  const blockedBy = findBlockers(task.dependsOn, taskStates).map(
    (dep) => `${step.key}.${dep}`
  );
  const isSkipped =
    skipped.has(key) || (step.optional && skipped.has(step.key));

  let state = SETUP_STATE.TODO;
  if (isCheckPassed(task.check, context)) state = SETUP_STATE.DONE;
  else if (!required && isSkipped) state = SETUP_STATE.SKIPPED;
  else if (blockedBy.length) state = SETUP_STATE.BLOCKED;
  taskStates.set(task.key, state);

  return {
    key,
    label: task.label,
    hint: task.hint ?? null,
    required,
    skippable: !required,
    state,
    ...(state === SETUP_STATE.BLOCKED && { blockedBy }),
  };
};

const evaluateStep = (step, context, skipped, flags, stepStates) => {
  const taskStates = new Map();
  const evaluatedTasks = step.tasks
    .filter((task) => isFlagEnabled(task.featureFlag, flags))
    .map((task) => evaluateTask(step, task, context, skipped, taskStates));
  const totals = summarize(evaluatedTasks);
  const blockedBy = findBlockers(step.dependsOn, stepStates);

  let state = SETUP_STATE.TODO;
  if (evaluatedTasks.every((task) => task.state === SETUP_STATE.DONE))
    state = SETUP_STATE.DONE;
  else if (step.optional && skipped.has(step.key)) state = SETUP_STATE.SKIPPED;
  else if (totals.done === totals.total) state = SETUP_STATE.DONE;
  else if (blockedBy.length) state = SETUP_STATE.BLOCKED;

  return {
    key: step.key,
    label: step.label,
    hint: step.hint,
    optional: Boolean(step.optional),
    state,
    ...(state === SETUP_STATE.BLOCKED && { blockedBy }),
    ...totals,
    tasks: evaluatedTasks,
  };
};

export const computeSetupProgress = ({
  context,
  skipped = [],
  enabledFlags = [],
}) => {
  const skippedSet = new Set(skipped);
  const flags = new Set(enabledFlags);
  const stepStates = new Map();

  const steps = SETUP_STEPS.filter(
    (step) =>
      isFlagEnabled(step.featureFlag, flags) &&
      step.tasks.some((task) => isFlagEnabled(task.featureFlag, flags))
  ).map((step) => {
    const evaluated = evaluateStep(
      step,
      context,
      skippedSet,
      flags,
      stepStates
    );
    stepStates.set(step.key, evaluated.state);
    return evaluated;
  });

  return {
    totals: summarize(steps.flatMap((step) => step.tasks)),
    steps,
  };
};
